import { TEAMS_LINK, TeamsLogo, WHATSAPP_NUMBER, WhatsAppLogo } from "@/lib";
import { motion } from "framer-motion";
import { 
    CheckCircle2, 
    Store, 
    Building2, 
    Globe2, 
    ArrowRight, 
    ShieldCheck, 
    Zap 
} from "lucide-react";

export default function WebsitePackagesSection() {
    
    const packages = [
        {
            id: 'starter',
            name: 'Digital Starter',
            target: 'Small Business & Personal Brands',
            icon: Store,
            description: 'Establish a credible online presence with a fast, mobile-responsive site that converts visitors.',
            features: [
                '5-Page Professional Website (Home, About, Services, Contact, etc.)',
                'Contact Form with Email Notifications',
                'Social Media & WhatsApp Integration',
                'Google Maps & Local SEO Setup',
                'Basic Booking/Table Reservation Link',
                'CMS for Easy Text/Image Updates',
                'SSL Security Certificate'
            ],
            cta: 'Get Started',
            popular: false
        },
        {
            id: 'growth',
            name: 'Business Growth',
            target: 'Agencies, SMEs & Retail',
            icon: Building2,
            description: 'A dynamic, data-driven platform designed to generate leads and streamline operations.',
            features: [
                '10-15 Page Dynamic Website',
                'Advanced CMS (Strapi/Contentful)',
                'Blog/News Section for SEO Growth',
                'CRM Integration (HubSpot/Mailchimp)',
                'Analytics Dashboard Setup',
                'Fast Loading (Core Web Vitals Optimized)',
                'Multi-Language Support',
                'Payment Gateway Integration'
            ],
            cta: 'Scale Your Business',
            popular: true
        },
        {
            id: 'enterprise',
            name: 'Enterprise Suite',
            target: 'Corporations & High-Traffic',
            icon: Globe2,
            description: 'Bespoke, scalable architecture built for high security, heavy traffic, and complex workflows.',
            features: [
                'Unlimited Pages & Custom Web Apps',
                'Microservices Architecture',
                'Single Sign-On (SSO) & Role Management',
                'Advanced Security (DDoS Protection, WAF)',
                'Custom API Development & Integration',
                'Automated CI/CD Pipelines',
                '24/7 Priority Support & SLA',
                'Dedicated DevOps Infrastructure'
            ],
            cta: 'Discuss Architecture',
            popular: false
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    return (
        <section id="web-solution" className="py-24 bg-gray-800 relative overflow-hidden">
            
            {/* Ambient Background Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-sky-900/10 rounded-full blur-[100px]" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <motion.span 
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm font-semibold tracking-wide uppercase mb-4"
                    >
                        <Zap className="w-4 h-4 mr-2" />
                        Web Development Services
                    </motion.span>
                    <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-6">
                        Engineered for <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-400">Real Impact</span>
                    </h2>
                    <p className="text-lg text-gray-400">
                        We don't just build websites; we build digital assets. Choose a tier that fits your current scale, from local startups to global enterprises.
                    </p>
                </div>

                {/* Packages Grid */}
                <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                >
                    {packages.map((pkg) => (
                        <motion.div
                            key={pkg.id}
                            variants={cardVariants}
                            className={`relative flex flex-col rounded-2xl transition-all duration-300 group
                                ${pkg.popular 
                                    ? 'bg-slate-800/60 border-2 border-indigo-500/50 shadow-2xl shadow-indigo-900/20' 
                                    : 'bg-slate-900/50 border border-slate-800 hover:border-indigo-500/30 hover:bg-slate-800/80'}
                            `}
                        >
                            {pkg.popular && (
                                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                                    <span className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg flex items-center">
                                        <SparklesIcon className="w-3 h-3 mr-1" /> Most Popular
                                    </span>
                                </div>
                            )}

                            <div className="p-8 flex-grow">
                                {/* Card Header */}
                                <div className="mb-6">
                                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4
                                        ${pkg.popular ? 'bg-indigo-500/20 text-indigo-300' : 'bg-slate-800 text-slate-400'}
                                    `}>
                                        <pkg.icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{pkg.name}</h3>
                                    <p className="text-sm font-medium text-indigo-400 mb-3">{pkg.target}</p>
                                    <p className="text-gray-400 text-sm leading-relaxed border-b border-gray-700/50 pb-6">
                                        {pkg.description}
                                    </p>
                                </div>

                                {/* Features List */}
                                <ul className="space-y-4 mb-8">
                                    {pkg.features.map((feature, i) => (
                                        <li key={i} className="flex items-start text-sm text-gray-300">
                                            <CheckCircle2 className={`w-5 h-5 mr-3 flex-shrink-0 ${pkg.popular ? 'text-green-400' : 'text-slate-500'}`} />
                                            <span className="leading-tight">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Action Buttons Area */}
                            <div className="p-8 pt-0 mt-auto space-y-3">
                                <motion.a
                                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Mohan, I'm interested in the ${pkg.name} package for my business. Can we discuss the details?`)}`}
                                    target="_blank"
                                    className={`w-full flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-200
                                        ${pkg.popular 
                                            ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-900/40' 
                                            : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'}
                                    `}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <WhatsAppLogo className="w-5 h-5 mr-2" />
                                    {pkg.cta}
                                </motion.a>
                                
                                <motion.a
                                    href={TEAMS_LINK}
                                    target="_blank"
                                    className="w-full flex items-center justify-center px-6 py-3 rounded-xl font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-all duration-200 text-sm border border-transparent hover:border-slate-700"
                                >
                                    <TeamsLogo className="w-4 h-4 mr-2 opacity-70" />
                                    Join Community Discussion
                                </motion.a>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Optional: Guarantee / Trust Footer */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    className="mt-16 flex flex-col md:flex-row items-center justify-center gap-8 text-gray-500 text-sm"
                >
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-emerald-500" />
                        <span>Secure Development</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-yellow-500" />
                        <span>High Performance</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Globe2 className="w-5 h-5 text-sky-500" />
                        <span>SEO Optimized</span>
                    </div>
                </motion.div>

            </div>
        </section>
    );
}

// Simple Sparkles Icon component for the "Popular" badge
function SparklesIcon({ className }: { className?: string }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
            <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813a3.75 3.75 0 002.576-2.576l.813-2.846A.75.75 0 019 4.5zM6 20.25a.75.75 0 01.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75h-.008a.75.75 0 01-.75-.75v-.008a.75.75 0 01-.75-.75h-.008a.75.75 0 01-.75-.75v-.008a.75.75 0 01.75-.75h.008z" clipRule="evenodd" />
        </svg>
    )
}